import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Crown, Users, Download, Phone, Mail, Trash2 } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Seo, SITE_URL } from '../components/Seo';
import { useAuth } from '../context/AuthContext';
import { api, pictureUrl, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { fieldCls } from '../components/member/ProfileForm';

const MemberRow = ({ m, onToggleAdmin, onDelete, selfEmail }) => {
  const pic = pictureUrl(m.picture);
  const cardUrl = `${SITE_URL}/kort/${m.card_code}`;
  return (
    <div data-testid={`admin-member-${m.user_id}`} className="rounded-3xl bg-[#1a1424] border border-white/5 p-5 flex flex-col sm:flex-row gap-5 items-center">
      <Link to={`/kort/${m.card_code}`} className="rounded-xl bg-white p-2 shrink-0" title="Opne medlemskort">
        <QRCodeCanvas value={cardUrl} size={84} level="M" />
      </Link>
      <div className="flex-1 min-w-0 w-full">
        <div className="flex items-center gap-3 mb-2">
          {pic ? <img src={pic} alt={m.name} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[#ff2e2e] text-black font-bold flex items-center justify-center">{m.name?.[0]?.toUpperCase()}</div>}
          <div className="min-w-0">
            <p className="text-white font-bold truncate flex items-center gap-2">
              {m.name}
              {m.is_admin && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ffb400] text-black text-[9px] font-bold uppercase tracking-widest"><Crown size={10} /> Admin</span>}
            </p>
            <p data-testid="admin-member-number" className="text-[#ff2e2e] font-bold tracking-wider text-sm">{m.member_number}</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <a href={`mailto:${m.email}`} title={m.email} className="text-white/75 hover:text-white inline-flex items-center gap-2 break-all"><Mail size={14} className="text-white/40 shrink-0" /> {m.email}</a>
          {m.phone ? (
            <a href={`tel:${m.phone}`} className="text-white/75 hover:text-white inline-flex items-center gap-2"><Phone size={14} className="text-white/40" /> {m.phone}</a>
          ) : (
            <span className="text-white/35 inline-flex items-center gap-2"><Phone size={14} /> Ikkje registrert</span>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
          <p className="text-white/35 text-xs">Registrert {m.created_at ? new Date(m.created_at).toLocaleDateString('nn-NO') : ''} · {m.auth_provider}</p>
          {m.is_env_admin ? (
            <span className="text-[10px] text-white/35 uppercase tracking-widest">Fast administrator</span>
          ) : m.email !== selfEmail && (<div className="flex items-center gap-2">
            <button type="button" onClick={() => onDelete(m)} data-testid={`delete-member-${m.user_id}`} aria-label="Slett medlem" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold border border-white/15 text-white/60 hover:border-red-500 hover:text-red-400 transition-colors"><Trash2 size={12} /> Slett</button>
            <button type="button" onClick={() => onToggleAdmin(m)} data-testid={`toggle-admin-${m.user_id}`} className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold border transition-colors ${m.is_admin ? 'border-white/15 text-white/60 hover:border-red-500 hover:text-red-400' : 'border-[#ffb400]/60 text-[#ffb400] hover:bg-[#ffb400] hover:text-black'}`}>
              <Crown size={12} /> {m.is_admin ? 'Fjern admin' : 'Gjer til admin'}
            </button>
          </div>)}
        </div>
      </div>
    </div>
  );
};

const AdminMembers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (user === false) navigate('/login', { replace: true });
    if (user && !user.is_admin) navigate('/medlem', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get('/admin/members').then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, [user]);

  const deleteMember = async (m) => {
    if (!window.confirm(`Slette medlemmen ${m.name} (${m.email}) for godt? Dette kan ikkje angrast.`)) return;
    try {
      await api.delete(`/admin/members/${m.user_id}`);
      setMembers(members.filter((x) => x.user_id !== m.user_id));
      toast.success('Medlemmen er sletta');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleAdmin = async (m) => {
    const promote = !m.is_admin;
    if (!window.confirm(promote ? `Gjere ${m.name} til administrator?` : `Fjerne administratorrettane til ${m.name}?`)) return;
    try {
      if (promote) await api.post('/admin/admins', { email: m.email });
      else await api.delete(`/admin/admins/${encodeURIComponent(m.email)}`);
      setMembers(members.map((x) => (x.user_id === m.user_id ? { ...x, is_admin: promote } : x)));
      toast.success(promote ? 'Ny administrator lagt til' : 'Administratorrettar fjerna');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    if (!members) return [];
    const s = q.trim().toLowerCase();
    if (!s) return members;
    return members.filter((m) => [m.name, m.email, m.phone, m.member_number].some((v) => (v || '').toLowerCase().includes(s)));
  }, [members, q]);

  const exportCsv = () => {
    const rows = [['Medlemsnummer', 'Namn', 'E-post', 'Telefon', 'Registrert'], ...filtered.map((m) => [m.member_number, m.name, m.email, m.phone || '', m.created_at || ''])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = 'refuel-medlemmer.csv';
    a.click();
  };

  if (!user?.is_admin) return <div className="min-h-screen bg-black" />;

  return (
    <div className="bg-black min-h-screen text-white" data-testid="admin-members-page">
      <Seo title="Medlemsoversikt (admin) | Refuel Weekend" description="Administrasjon av medlemmer." path="/admin/medlemmer" noindex />
      <Navbar />
      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-12">
        <Link to="/admin" data-testid="admin-back-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm mb-8"><ArrowLeft size={16} /> Adminpanel</Link>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-[#ffb400] font-bold uppercase tracking-widest text-sm mb-2 inline-flex items-center gap-2"><Crown size={14} /> Administrator</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>Medlemsoversikt</h1>
            <p className="text-white/60 mt-2 inline-flex items-center gap-2"><Users size={16} /> <span data-testid="admin-members-count">{members ? members.length : '…'}</span> medlemmer</p>
          </div>
          <button onClick={exportCsv} data-testid="admin-export-button" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-white/20 text-white font-bold hover:border-[#ffb400] hover:text-[#ffb400] transition-colors"><Download size={16} /> Eksporter CSV</button>
        </div>
        <div className="relative mb-6">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk på namn, e-post, telefon eller medlemsnummer" className={`${fieldCls} pl-12`} />
        </div>
        {members === null ? (
          <p className="text-white/50">Lastar medlemmer…</p>
        ) : filtered.length === 0 ? (
          <p className="text-white/50" data-testid="admin-members-empty">Ingen treff.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-members-list">
            {filtered.map((m) => <MemberRow key={m.user_id} m={m} selfEmail={user.email} onToggleAdmin={toggleAdmin} onDelete={deleteMember} />)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AdminMembers;
